import { useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { TextFieldContext } from 'react-aria-components';
import { IconButton } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import clsx from 'clsx';

import type {
  FieldOptions,
  FilterToken,
  FlattenedToken,
  ParsedToken,
  RelationalFieldOption,
} from '../../types/QueryBuilder';

import { useQueryBuilderContext } from './context';
import { FilterBarContext, useFilterBarContext } from './FilterBarContext';
import type { FilterBarContextValue } from './FilterBarContext';
import { useApplyFocusOverride } from './hooks/useApplyFocusOverride';
import { useGridSelection } from './hooks/useGridSelection';
import { useKeyboardSelection, KeyboardSelectionContext } from './hooks/useKeyboardSelection';
import { useUndoStack } from './hooks/useUndoStack';
import { SelectionKeyHandler } from './SelectionKeyHandler';
import { FreeTextToken } from './tokens/FreeTextToken';
import { LogicBoolToken } from './tokens/LogicBoolToken';
import { LogicParen } from './tokens/LogicParen';
import { LozengeFilter } from './tokens/LozengeFilter';
import { flattenTokens } from './utils';

import styles from './FilterBar.module.css';

export { useFilterBarContext };
export type { FilterBarContextValue };

type FilterBarProps = {
  /** When true, the bar is rendered read-only: token cells are disabled, popovers don't
   * open, remove buttons are hidden, and the cursor-landing FreeText inputs are skipped.
   * Defaults to false (editable). */
  readonly?: boolean;
  /** Field options used both for FreeTextToken key autocomplete and per-token LozengeFilter value autocomplete. */
  fieldOptions?: FieldOptions[];
  /** When true, renders a search icon in the left slot. */
  showSearchIcon?: boolean;
  /** When provided, applied as the `id` of the trailing FreeTextToken input for label association. */
  inputId?: string;
  /**
   * Id of a visible label element used to name the bar's `role="textbox"` container via
   * `aria-labelledby`. Preferred over a `<label htmlFor>` pointing at the inner input, since the
   * focusable element is the container div (not a labelable form element). Replaces the default
   * "Search query" aria-label when set.
   */
  ariaLabelledby?: string;
  /**
   * When false, suppresses the inline type chip (`string` / `number` / `boolean`)
   * next to field-key autocomplete options. Defaults to true. Useful for field
   * sets where the underlying value type isn't meaningful to the user.
   */
  showTypeLabels?: boolean;
  /**
   * Optional list of typed-group prefixes (e.g. anySpan / noSpan). When provided
   * (and non-empty) the FreeText autocomplete surfaces a "Relational Fields"
   * section, and the LogicParen type cell offers them in its picker. When
   * omitted, no Relational Fields UI is shown.
   */
  relationalFieldOptions?: RelationalFieldOption[];
  /** Placeholder for the trailing FreeText input while the bar is empty; omitted = none. */
  placeholder?: string;
  /**
   * Renders the bar with the standard Launchpad invalid border. When omitted, the
   * bar reads `isInvalid` from a surrounding `TextFieldContext` (e.g. set by a
   * Launchpad `FieldGroup` with `errorMessage`) so wrapping it in a FieldGroup
   * gives the same red-outline behavior as a regular input.
   */
  isInvalid?: boolean;
  /** Applied to the bar's outer element so consumers can override its styling (e.g. a compact inline variant). */
  className?: string;
  /** Footer pinned inside the field-key autocomplete popover. Omitted = no footer. */
  autocompleteFooter?: React.ReactNode;
};

/**
 * Returns the character offset just after the end of a flattened token in the query string.
 * Used to compute the insertion point for auto-inserted FreeTextTokens.
 */
function getFlatTokenQueryEnd(token: FlattenedToken): number {
  if (token.type === 'PAREN') {
    if (token.side === 'open') {
      // Insert just inside the opening parenthesis. The expression's start offset
      // points at the FIRST character of the prefix (when present, e.g. `anySpan(`),
      // so we need to skip the prefix's length AND the literal `(` to land between
      // the parens — otherwise content gets spliced in mid-prefix.
      const prefixLen = token.expression.expressionType?.length ?? 0;
      return token.expression.location.start.offset + prefixLen + 1;
    }
    // close paren: insert just after the closing parenthesis
    return token.expression.location.end.offset;
  }
  return token.location.end.offset;
}

/**
 * Build the full ordered key list for the grid, mirroring the rendering logic.
 * Includes auto-inserted FREE_TEXT token keys ("ft-start", "ft-after-N") interleaved
 * with content token keys (String(N)) so the selection system can reason about boundaries.
 */
function buildAllKeys(flatTokens: FlattenedToken[]): string[] {
  const keys: string[] = [];
  if (flatTokens.length === 0) {
    keys.push('ft-start');
    return keys;
  }
  if (flatTokens[0].type !== 'FREE_TEXT') {
    keys.push('ft-start');
  }
  flatTokens.forEach((token, i) => {
    keys.push(String(i));
    const next = flatTokens[i + 1];
    if (token.type !== 'FREE_TEXT' && (next === undefined || next.type !== 'FREE_TEXT')) {
      keys.push(`ft-after-${i}`);
    }
  });
  return keys;
}

/** Returns true for keys that represent FREE_TEXT cursor-landing tokens. */
function makeIsFreeTextKey(flatTokens: FlattenedToken[]): (key: string) => boolean {
  return (key: string) => {
    if (key.startsWith('ft-')) {
      return true;
    }
    const i = parseInt(key, 10);
    if (isNaN(i)) {
      return false;
    }
    return flatTokens[i]?.type === 'FREE_TEXT';
  };
}

type FilterParsedToken = Extract<ParsedToken, { type: 'FILTER' }>;

/**
 * Convert a parsed FILTER token into the FilterToken shape consumed by LozengeFilter.
 * Multi-value tokens keep their array shape so individual values (including any that
 * contain commas) survive the round-trip instead of collapsing into one string.
 */
function toFilterToken(token: FilterParsedToken): FilterToken {
  return { field: token.field, operator: token.operator, value: token.value };
}

export function FilterBar({
  readonly = false,
  fieldOptions,
  showSearchIcon = false,
  inputId,
  showTypeLabels = true,
  relationalFieldOptions,
  isInvalid,
  className,
  placeholder,
  autocompleteFooter,
  ariaLabelledby,
}: FilterBarProps) {
  const { dispatch, focusOverride, wrapperRef, query, committedQuery, parsedQuery } = useQueryBuilderContext();
  const textFieldContext = useContext(TextFieldContext);
  const contextIsInvalid =
    textFieldContext && 'isInvalid' in textFieldContext ? Boolean(textFieldContext.isInvalid) : false;
  const invalid = isInvalid ?? contextIsInvalid;

  const flatTokens = flattenTokens(parsedQuery);
  // Stable ref so the drag-selection effect always reads the latest flatTokens value.
  const flatTokensRef = useRef(flatTokens);
  flatTokensRef.current = flatTokens;

  const allKeys = buildAllKeys(flatTokens);
  const isFreeTextKey = makeIsFreeTextKey(flatTokens);

  const outerRef = useRef<HTMLDivElement>(null);
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  // role="grid" requires role="row" descendants. The grid attributes are applied below
  // only when there's at least one rendered token row — see the gridProps narrowing
  // after `renderedTokens` is built.

  const gridSelection = useGridSelection();
  // Stable ref so the drag-selection RAF callback always reads the latest state.
  const gridSelectionRef = useRef(gridSelection);
  gridSelectionRef.current = gridSelection;

  // When the selection becomes non-empty, move DOM focus to the hidden input and
  // clear the virtual focused key so no individual token row appears active.
  // Depend on the size primitive (not the Set reference) so unrelated dispatches
  // that produce a new selectedKeys Set with the same contents don't re-steal focus.
  const selectionSize = gridSelection.selectedKeys.size;
  useEffect(() => {
    if (selectionSize > 0) {
      gridSelectionRef.current.setFocusedKey(null);
      hiddenInputRef.current?.focus();
    }
  }, [selectionSize]);

  useApplyFocusOverride(focusOverride, dispatch, gridSelection, wrapperRef, allKeys);

  // After a commit, move focus to the trailing FreeText input so the user can chain
  // another filter without re-clicking. Skip when:
  //  - focusOverride is set: the commit already targets focus (e.g. inside a new group).
  //  - focus already left the bar (onBlur, or a removed lozenge dropped focus to body).
  //  - a selection is active: the hidden selection input owns focus.
  //  - readonly: no editable trailing input exists.
  const prevCommittedRef = useRef(committedQuery);
  useLayoutEffect(() => {
    const prevCommitted = prevCommittedRef.current;
    prevCommittedRef.current = committedQuery;
    if (readonly || committedQuery === prevCommitted || focusOverride !== null) {
      return;
    }
    const outer = outerRef.current;
    if (!outer || !outer.contains(document.activeElement)) {
      return;
    }
    if (gridSelectionRef.current.selectedKeys.size > 0) {
      return;
    }
    const trailingKey = allKeys.at(-1);
    if (trailingKey === undefined) {
      return;
    }
    outer.querySelector<HTMLInputElement>(`input[data-key="${CSS.escape(trailingKey)}"]`)?.focus();
  }, [committedQuery, focusOverride, readonly, allKeys]);

  const { undo, redo } = useUndoStack({ query: query.trim(), focusOverride, state: gridSelection, dispatch });

  const keyboardSelCtxValue = useKeyboardSelection({
    state: gridSelection,
    allKeys,
    isFreeTextKey,
    flatTokens,
    hiddenInputRef,
  });

  // Stable ref so the drag-selection effect can always read the latest context.
  const selCtxRef = useRef(keyboardSelCtxValue);
  selCtxRef.current = keyboardSelCtxValue;

  // Set by the drag-selection mouseup handler when the just-completed gesture was a real drag
  // (not a click). The click event that follows mouseup checks this and bails so it doesn't
  // refocus an input — refocusing would blur the SelectionKeyHandler hidden input, whose
  // onBlur clears the selection that the drag just produced.
  const dragJustCompletedRef = useRef(false);

  function handleClick(e: React.MouseEvent) {
    // Don't hijack clicks on interactive elements — let them handle their own focus.
    if ((e.target as Element).closest('input, button, [role="button"], [role="combobox"]')) {
      return;
    }
    // Suppress the click that fires immediately after a drag-selection mouseup.
    if (dragJustCompletedRef.current) {
      dragJustCompletedRef.current = false;
      return;
    }
    const grid = gridRef.current;
    if (!grid) {
      return;
    }

    // Recover flex-wrap visual rows by grouping direct children by offsetTop —
    // siblings that wrap to the same row share the same value.
    const rowMap = new Map<number, HTMLElement[]>();
    for (const child of Array.from(grid.children) as HTMLElement[]) {
      const list = rowMap.get(child.offsetTop);
      if (list) {
        list.push(child);
      } else {
        rowMap.set(child.offsetTop, [child]);
      }
    }
    const rows = [...rowMap.values()];
    if (rows.length === 0) {
      return;
    }

    // Hit-test the click Y against each row. The last row's lower bound is the grid's
    // bottom so clicks in trailing whitespace at the end of a row still register.
    const gridBottom = grid.getBoundingClientRect().bottom;
    let targetRow: HTMLElement[] | null = null;
    for (let i = 0; i < rows.length; i++) {
      const rowTop = rows[i][0].getBoundingClientRect().top;
      const rowBottom = i + 1 < rows.length ? rows[i + 1][0].getBoundingClientRect().top : gridBottom;
      if (e.clientY >= rowTop && e.clientY < rowBottom) {
        targetRow = rows[i];
        break;
      }
    }

    // Click landed outside any row (e.g. in outer padding around the grid) — fall back
    // to the last row so the filter bar still feels clickable.
    const focusRow = targetRow ?? rows[rows.length - 1];
    const inputs = focusRow.flatMap((_el) => Array.from(_el.querySelectorAll<HTMLInputElement>('input')));
    if (inputs.length === 0) {
      return;
    }

    // Of the inputs in that row, focus the one whose horizontal center is closest to
    // the click X — this makes a click between two tokens land on the nearer input.
    let closest = inputs[0];
    let closestDistance = Infinity;
    for (const input of inputs) {
      const rect = input.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      const distance = Math.abs(center - e.clientX);
      if (distance < closestDistance) {
        closestDistance = distance;
        closest = input;
      }
    }
    closest.focus();
  }

  // Drag selection — native DOM listeners so we aren't limited by React's synthetic
  // event system. Skipped entirely in readonly mode: a readonly bar shouldn't let
  // users select lozenges as a unit (only their inner text remains selectable via
  // normal browser text selection).
  useEffect(() => {
    if (readonly) {
      return;
    }
    const elOrNull = gridRef.current;
    if (!elOrNull) {
      return;
    }
    // Bind to a non-null alias so inner function closures below keep the narrowed type.
    const el: HTMLDivElement = elOrNull;

    let isDown = false;
    let startX = 0;
    let startY = 0;
    let dragging = false;
    let animFrame: number | null = null;
    let rectCache: Array<{ key: string; rect: DOMRect }> | null = null;

    function handleMouseDown(e: MouseEvent) {
      // Gate: a mousedown inside a content-token row should not start drag selection.
      if ((e.target as Element).closest('[data-token-container]')) {
        return;
      }
      isDown = true;
      startX = e.clientX;
      startY = e.clientY;
      dragging = false;
    }

    function handleMouseMove(e: MouseEvent) {
      if (!isDown) {
        return;
      }
      const dx = Math.abs(e.clientX - startX);
      const dy = Math.abs(e.clientY - startY);

      if (!dragging) {
        if (dx > 10 || dy > 10) {
          dragging = true;
          // Cache the bounding rect of every keyed token element for the drag duration.
          rectCache = Array.from(el.querySelectorAll<HTMLElement>('[data-key]')).flatMap((tokenEl) => {
            const { key } = tokenEl.dataset;
            if (key === undefined) {
              return [];
            }
            return [{ key, rect: tokenEl.getBoundingClientRect() }];
          });
          // Suppress hover/click effects on content tokens during the drag.
          el.querySelectorAll<HTMLElement>('[data-token-container]').forEach((tokenEl) => {
            const { style } = tokenEl;
            style.pointerEvents = 'none';
          });
        } else {
          return;
        }
      }

      if (animFrame !== null) {
        return;
      }
      const currentX = e.clientX;
      const currentY = e.clientY;
      animFrame = requestAnimationFrame(() => {
        animFrame = null;
        if (!rectCache) {
          return;
        }
        const minX = Math.min(startX, currentX);
        const maxX = Math.max(startX, currentX);
        const minY = Math.min(startY, currentY);
        const maxY = Math.max(startY, currentY);
        const selected = new Set<string>();
        for (const { key, rect } of rectCache) {
          // Select every token whose row overlaps the dragged rectangle.
          if (rect.right >= minX && rect.left <= maxX && rect.bottom >= minY && rect.top <= maxY) {
            selected.add(key);
          }
        }
        // Fill in any FREE_TEXT boundary tokens between the leftmost and rightmost
        // selected keys so the selection appears continuous.  FreeText inputs are
        // narrow and are easily missed by the drag rect, but conceptually anything
        // spanning the selected range should be included.
        if (selected.size > 0) {
          const keys = selCtxRef.current.allKeys;
          const indices = Array.from(selected)
            .map((k) => keys.indexOf(k))
            .filter((i) => i !== -1);
          if (indices.length > 0) {
            const lo = Math.min(...indices);
            const hi = Math.max(...indices);
            for (let i = lo; i <= hi; i++) {
              selected.add(keys[i]);
            }
          }
        }
        selCtxRef.current.state.setSelectedKeys(selected);
      });
    }

    function handleMouseUp() {
      if (dragging) {
        // Re-enable pointer events and discard the rect cache.
        el.querySelectorAll<HTMLElement>('[data-token-container]').forEach((tokenEl) => {
          const { style } = tokenEl;
          style.pointerEvents = '';
        });
        // Tell handleClick (which fires next on the wrapper) to skip its refocus. A drag
        // produces a selection; refocusing an input here would blur the hidden selection
        // handler and clear the selection.
        dragJustCompletedRef.current = true;
      }
      isDown = false;
      dragging = false;
      rectCache = null;
      if (animFrame !== null) {
        cancelAnimationFrame(animFrame);
        animFrame = null;
      }
    }

    el.addEventListener('mousedown', handleMouseDown);
    el.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      if (animFrame !== null) {
        cancelAnimationFrame(animFrame);
      }
      el.removeEventListener('mousedown', handleMouseDown);
      el.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable via refs; readonly intentionally re-binds the effect
  }, [readonly]);

  // Auto-render the flattened token list. FreeTextTokens are interleaved between
  // content tokens only when editable — readonly bars wouldn't accept typed input
  // anyway, and rendering them as live <input>s defeats the read-only intent.
  const renderedTokens: React.ReactNode[] = [];
  if (!readonly && (flatTokens.length === 0 || flatTokens[0].type !== 'FREE_TEXT')) {
    renderedTokens.push(
      <FreeTextToken
        key="ft-start"
        tokenKey="ft-start"
        options={fieldOptions}
        queryInsertOffset={0}
        isTrailing={flatTokens.length === 0}
        // Only the empty bar shows the placeholder — once a token exists, a
        // leading FreeText cell is just a cursor-landing gap and shouldn't read
        // like an empty input.
        {...(flatTokens.length === 0 ? { placeholder } : {})}
      />,
    );
  }
  flatTokens.forEach((token, i) => {
    const tokenKey = String(i);
    if (token.type === 'FILTER') {
      renderedTokens.push(
        <LozengeFilter
          key={tokenKey}
          tokenKey={tokenKey}
          value={toFilterToken(token)}
          onChange={() => {}}
          onRemove={() => dispatch({ type: 'DELETE_TOKEN', token })}
          removable={!readonly}
          options={fieldOptions}
          parsedToken={token}
        />,
      );
    } else if (token.type === 'LOGIC_BOOL') {
      renderedTokens.push(
        <LogicBoolToken
          key={tokenKey}
          tokenKey={tokenKey}
          token={token}
          onChange={() => {}}
          onRemove={() => dispatch({ type: 'DELETE_TOKEN', token })}
          removable={!readonly}
        />,
      );
    } else if (token.type === 'PAREN') {
      renderedTokens.push(
        <LogicParen
          key={tokenKey}
          tokenKey={tokenKey}
          token={token}
          onRemove={() => dispatch({ type: 'UNWRAP_EXPRESSION', token: token.expression })}
          removable={!readonly && token.side === 'close'}
          expressionType={token.expression.expressionType}
        />,
      );
    } else if (token.type === 'FREE_TEXT') {
      renderedTokens.push(
        <FreeTextToken
          key={tokenKey}
          tokenKey={tokenKey}
          initialValue={token.value}
          options={fieldOptions}
          parsedToken={token}
        />,
      );
    }

    if (!readonly && token.type !== 'FREE_TEXT') {
      const next = flatTokens[i + 1];
      if (next === undefined || next.type !== 'FREE_TEXT') {
        renderedTokens.push(
          <FreeTextToken
            key={`ft-after-${i}`}
            tokenKey={`ft-after-${i}`}
            options={fieldOptions}
            queryInsertOffset={getFlatTokenQueryEnd(token)}
            isTrailing={next === undefined}
          />,
        );
      }
    }
  });

  const gridProps: React.HTMLAttributes<HTMLDivElement> =
    renderedTokens.length > 0 ? { role: 'grid', 'aria-label': 'Create a search query' } : {};

  return (
    <FilterBarContext.Provider
      value={{
        readonly,
        // Readonly bars opt out of grid selection — descendants that read this
        // (via useQueryBuilderGridItem) skip the data-key / aria-selected wiring,
        // so lozenges and parens are no longer addressable as selectable rows.
        gridState: readonly ? null : gridSelection,
        inputId,
        showTypeLabels,
        relationalFieldOptions,
        placeholder,
        autocompleteFooter,
      }}
    >
      <KeyboardSelectionContext.Provider value={keyboardSelCtxValue}>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,
        jsx-a11y/no-noninteractive-element-interactions */}
        <div
          role="textbox"
          aria-label={ariaLabelledby ? undefined : 'Search query'}
          aria-labelledby={ariaLabelledby}
          tabIndex={0}
          aria-readonly={readonly || undefined}
          onClick={handleClick}
          onKeyDown={(e) => {
            // Undo (Cmd/Ctrl+Z) and redo (Cmd/Ctrl+Shift+Z, Ctrl+Y), handled on the outer
            // container — not the grid — so they fire whether focus is in a grid input or on
            // the hidden SelectionKeyHandler input (a sibling of the grid, whose events never
            // reach grid-level handlers). Bubble phase (not capture) so a focused free-text
            // input with uncommitted text can stopPropagation first and keep native input
            // undo/redo while it's dirty (see FreeTextToken).
            // Skip on readonly bars: they're display-only (e.g. the AI-suggestion preview
            // driven by a controlled `value` with no onChange), so a query-history undo/redo
            // would revert the internal state and desync the preview from its `value` prop.
            const isZ = e.key === 'z' || e.key === 'Z';
            const isRedo =
              !readonly && (e.ctrlKey || e.metaKey) && ((isZ && e.shiftKey) || e.key === 'y' || e.key === 'Y');
            const isUndo = !readonly && (e.ctrlKey || e.metaKey) && isZ && !e.shiftKey;
            if (isRedo || isUndo) {
              e.preventDefault();
              e.stopPropagation();
              // Drop any active selection first — the restored query re-parses into new
              // tokens, so stale selected keys would highlight the wrong rows.
              if (gridSelectionRef.current.selectedKeys.size > 0) {
                gridSelectionRef.current.setSelectedKeys(new Set());
              }
              if (isRedo) {
                redo();
              } else {
                undo();
              }
              return;
            }
            // Cmd/Ctrl+A: select every token in the grid when focus is on the wrapper itself
            // or on a button descendant (e.g. a LozengeFilter cell button). FreeTextToken's
            // input handler runs first and preventDefaults; defaultPrevented short-circuits us.
            if (e.defaultPrevented) {
              return;
            }
            if (!readonly && (e.ctrlKey || e.metaKey) && e.key === 'a') {
              // Skip when the event target is an <input>: that means the user is editing
              // a cell's text and Cmd+A should select-all within the input natively, not
              // promote to a whole-query selection.
              if ((e.target as HTMLElement).tagName === 'INPUT') {
                return;
              }
              const ctx = selCtxRef.current;
              if (!ctx) {
                return;
              }
              const hasStructuredTokens = ctx.allKeys.some((k) => !ctx.isFreeTextKey(k));
              if (hasStructuredTokens) {
                e.preventDefault();
                ctx.state.selectAll(ctx.allKeys);
              }
            }
          }}
          ref={outerRef}
          className={clsx(styles.filterBarOuter, { [styles.readonly]: readonly }, className)}
          data-editable={readonly ? undefined : true}
          data-invalid={invalid || undefined}
          onBlur={(e) => {
            // Commit the query when focus leaves the filter bar entirely.
            if (!outerRef.current?.contains(e.relatedTarget as Node)) {
              dispatch({ type: 'COMMIT_QUERY' });
            }
          }}
        >
          {showSearchIcon && (
            <span className={styles.searchIconWrapper}>
              <Icon name="search" size="small" className={styles.searchIcon} aria-hidden />
            </span>
          )}
          {/* SelectionKeyHandler renders a visually-hidden <input>. It must NOT live inside
              the role="grid" wrapper — axe flags non-row children of role="grid". */}
          <SelectionKeyHandler inputRef={hiddenInputRef} gridRef={gridRef} />
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- role="grid" is provided via gridProps spread */}
          <div ref={gridRef} {...gridProps} data-filter-bar-row className={styles.filterBar}>
            {renderedTokens}
          </div>
          {!readonly && query.trim() && (
            <IconButton
              icon="cancel-circle-outline"
              variant="minimal"
              size="small"
              aria-label="Clear filter"
              className={styles.clearButton}
              onPress={() => {
                dispatch({ type: 'UPDATE_QUERY', query: '' });
              }}
            />
          )}
        </div>
      </KeyboardSelectionContext.Provider>
    </FilterBarContext.Provider>
  );
}

FilterBar.displayName = 'FilterBar';
